/**
 * Arch Hangar Template Parameters
 *
 * Defines form parameters and Zod schema for arch/Quonset-style hangars.
 * Semi-cylindrical structure with optional knee walls and end-wall doors.
 */

import { z } from 'zod'
import type { BaseTemplateParams, DoorType, DoorSpec } from '../types'

// ============================================================================
// TYPES
// ============================================================================

/** Arch cross-section profile type */
export type ArchProfile = 'semicircular' | 'parabolic'

/** End wall door configuration (front or back) */
export interface ArchDoorConfig {
  enabled: boolean
  width_ft: number
  height_ft: number
  type: DoorType
}

/** Arch hangar template parameters */
export interface ArchHangarParams extends BaseTemplateParams {
  /** Clear span width at the base (ft) */
  span_ft: number
  /** Length of the building along extrusion axis (ft) */
  length_ft: number
  /** Height at the arch apex above knee wall (ft) */
  crown_height_ft: number
  /** Vertical straight section before curve starts (ft) */
  knee_wall_height_ft: number
  /** Arch cross-section profile */
  arch_profile: ArchProfile
  /** Number of segments for curve approximation */
  arch_segments: number
  /** Shell/wall thickness (ft) */
  wall_thickness_ft: number
  /** Front end wall door */
  front_door: ArchDoorConfig
  /** Back end wall door */
  back_door: ArchDoorConfig
}

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

const archDoorConfigSchema = z.object({
  enabled: z.boolean(),
  width_ft: z.number().min(10, 'Door width must be at least 10 ft').max(200),
  height_ft: z.number().min(8, 'Door height must be at least 8 ft').max(100),
  type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
})

export const archHangarParamsSchema = z.object({
  // Base params
  name: z.string().min(1, 'Name is required').max(100),
  code: z.string().min(1, 'Code is required').max(20),
  latitude: z.number(),
  longitude: z.number(),
  bearing_deg: z.number().transform((val) => ((val % 360) + 360) % 360),
  floor_color: z.string(),
  wall_color: z.string(),

  // Arch dimensions
  span_ft: z.number().min(30, 'Span must be at least 30 ft').max(200),
  length_ft: z.number().min(30, 'Length must be at least 30 ft').max(500),
  crown_height_ft: z.number().min(5, 'Crown height must be at least 5 ft').max(200),
  knee_wall_height_ft: z.number().min(0).max(15, 'Knee wall max 15 ft'),
  arch_profile: z.enum(['semicircular', 'parabolic']),
  arch_segments: z.number().int().min(12).max(48),
  wall_thickness_ft: z.number().min(0.25).max(2),

  // Doors
  front_door: archDoorConfigSchema,
  back_door: archDoorConfigSchema,
})

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_ARCH_HANGAR_VALUES: ArchHangarParams = {
  name: 'New Arch Hangar',
  code: 'ARC',
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
  floor_color: '#808080',
  wall_color: '#A0A0A0',

  span_ft: 80,
  length_ft: 100,
  crown_height_ft: 40, // Half of span = semicircle
  knee_wall_height_ft: 0,
  arch_profile: 'semicircular',
  arch_segments: 24,
  wall_thickness_ft: 0.5,

  front_door: {
    enabled: true,
    width_ft: 60,
    height_ft: 20,
    type: 'bifold',
  },
  back_door: {
    enabled: false,
    width_ft: 40,
    height_ft: 16,
    type: 'rolling',
  },
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Calculate the maximum allowable door height at a given x-position.
 * The arch profile height varies with horizontal position.
 */
export function getMaxDoorHeight(params: ArchHangarParams): number {
  // Door is centered on the end wall; the constraining height is at the door edges
  // For a centered door, the minimum arch height is at ±doorWidth/2
  return params.knee_wall_height_ft + params.crown_height_ft
}

/**
 * Convert ArchDoorConfig to DoorSpec for storage/rendering
 */
export function archDoorToDoorSpec(
  door: ArchDoorConfig,
  wallWidth: number
): DoorSpec | null {
  if (!door.enabled) return null
  const doorWidth = Math.min(door.width_ft, wallWidth - 4)
  return {
    offset_ft: (wallWidth - doorWidth) / 2,
    width_ft: doorWidth,
    height_ft: door.height_ft,
    type: door.type,
  }
}
